import { defineStore } from 'pinia';
import request from '@/utils/request';

interface UserState {
  token: string | null;
  userInfo: any | null;
  captchaId: string;
}

interface LoginData {
  username: string;
  password: string;
  lotNumber?: string;
  captchaOutput?: string;
  passToken?: string;
  genTime?: string;
}

interface EmailLoginData {
  email: string;
  code: string;
  lotNumber: string;
  captchaOutput: string;
  passToken: string;
  genTime: string;
}

interface UserInfo {
  id: string;
  username: string;
  email: string;
  phone: string;
  avatar: string;
  bio: string;
}

interface PasswordForm {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface LoginResponse {
  token: string;
  userInfo: UserInfo;
}

interface CaptchaResponse {
  id: string;
  image: string;
}

interface SendEmailCodeRequest {
  email: string;
  captchaCode: string;
  captchaId: string;
}

interface SendEmailCodeWithGeetestRequest {
  email: string;
  lotNumber: string;
  captchaOutput: string;
  passToken: string;
  genTime: string;
}

interface RegisterRequest {
  email: string;
  emailCode: string;
  lotNumber: string;
  captchaOutput: string;
  passToken: string;
  genTime: string;
}

interface ApiResponse<T> {
  data: T;
  message?: string;
  code?: number;
}

interface UpdateEmailRequest {
  newEmail: string;
  emailCode: string;
}

interface QQLoginRequest {
  accessToken: string;
  state: string;
}

interface QQBindRequest {
  accessToken: string;
}

interface QQBindingResponse {
  bound: boolean;
  nickname?: string;
  avatar?: string;
}

interface GoogleLoginRequest { code: string }
interface GithubLoginRequest { code: string }
interface GoogleBindingResponse { bound: boolean; name?: string; email?: string; picture?: string }
interface GithubBindingResponse { bound: boolean; login?: string; name?: string; avatarUrl?: string }
interface BohriumBindingResponse {
  bound: boolean;
  nickname?: string;
  orgId?: string;
  email?: string;
  extraEmail?: boolean;
  maskedPhone?: string;
}

interface UserSessionResponse {
  sessionId: string;
  deviceName: string;
  deviceType: string;
  browser: string;
  os: string;
  ipAddress: string;
  location?: string;
  isCurrent: boolean;
  lastActivity: string;
  createdAt: string;
}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    token: localStorage.getItem('token'),
    userInfo: null,
    captchaId: '',
  }),

  actions: {
    // 管理员：按用户ID查询其客户端
    async adminListClientsByOwner(ownerUserId: number) {
      try {
        const response = await request.get<void, ApiResponse<any[]>>(`/admin/oauth2/clients/owner/${ownerUserId}`)
        if (response.code !== 200) throw new Error(response.message || '获取客户端失败')
        return response.data || []
      } catch (e: any) {
        throw new Error(e.message || '获取客户端失败')
      }
    },

    // 管理员：创建客户端
    async adminCreateClient(payload: { ownerUserId: number, name: string, description?: string, redirectUri: string, scope?: string, grantTypes?: string }) {
      try {
        const response = await request.post<typeof payload, ApiResponse<any>>('/admin/oauth2/clients', payload)
        if (response.code !== 200) throw new Error(response.message || '创建客户端失败')
        return response.data
      } catch (e: any) {
        throw new Error(e.message || '创建客户端失败')
      }
    },

    // 管理员：创建客户端（可自定义clientId）
    async adminCreateClientWithId(payload: { ownerUserId: number, clientId: string, name: string, description?: string, redirectUri: string, scope?: string, grantTypes?: string }) {
      try {
        const response = await request.post<typeof payload, ApiResponse<any>>('/admin/oauth2/clients', payload)
        if (response.code !== 200) throw new Error(response.message || '创建客户端失败')
        return response.data
      } catch (e: any) {
        throw new Error(e.message || '创建客户端失败')
      }
    },

    // 管理员：列出全部客户端
    async adminListAllClients() {
      try {
        const response = await request.get<void, ApiResponse<any[]>>('/admin/oauth2/clients')
        if (response.code !== 200) throw new Error(response.message || '获取客户端失败')
        return response.data || []
      } catch (e: any) {
        throw new Error(e.message || '获取客户端失败')
      }
    },

    // 管理员：更新客户端（可编辑clientId等）
    async adminUpdateClient(id: number, payload: { clientId?: string, name?: string, description?: string, redirectUri?: string, scope?: string, grantTypes?: string }) {
      try {
        const response = await request.put<typeof payload, ApiResponse<any>>(`/admin/oauth2/clients/${id}`, payload)
        if (response.code !== 200) throw new Error(response.message || '更新客户端失败')
        return response.data
      } catch (e: any) {
        throw new Error(e.message || '更新客户端失败')
      }
    },

    // 管理员：重置密钥
    async adminResetClientSecret(id: number) {
      try {
        const response = await request.post<void, ApiResponse<any>>(`/admin/oauth2/clients/${id}/reset-secret`)
        if (response.code !== 200) throw new Error(response.message || '重置密钥失败')
        return response.data
      } catch (e: any) {
        throw new Error(e.message || '重置密钥失败')
      }
    },
    async adminRevealClientSecret(id: number, method: 'password' | 'email', credential: string) {
      const response = await request.post<any, ApiResponse<any>>(`/admin/oauth2/clients/${id}/secret/reveal`, {
        method,
        credential
      })
      if (response.code !== 200) throw new Error(response.message || '身份验证失败')
      return response.data
    },
    async adminSendClientSecretCode(id: number) {
      const response = await request.post<void, ApiResponse<any>>(`/admin/oauth2/clients/${id}/secret/email-code`)
      if (response.code !== 200) throw new Error(response.message || '验证码发送失败')
      return response.data
    },
    // 获取当前用户的 OIDC 客户端列表（支持 OAuth2 和 OIDC）
    async getMyOAuthClients() {
      try {
        const response = await request.get<void, ApiResponse<any[]>>('/user/oauth2/clients');
        if (response.code !== 200) {
          throw new Error(response.message || '获取客户端列表失败');
        }
        return response.data || [];
      } catch (error: any) {
        throw new Error(error.message || '获取客户端列表失败');
      }
    },

    async getOAuthClientSelfServiceEligibility() {
      try {
        const response = await request.get<void, ApiResponse<any>>('/user/oauth2/clients/self-service/eligibility');
        if (response.code !== 200) {
          throw new Error(response.message || '获取申请资格失败');
        }
        return response.data;
      } catch (error: any) {
        throw new Error(error.message || '获取申请资格失败');
      }
    },

    async createSelfServiceOAuthClient(name: string, protocolVersion: 'MODERN' | 'LEGACY' = 'MODERN') {
      try {
        const response = await request.post<{ name: string; protocolVersion: string }, ApiResponse<any>>('/user/oauth2/clients/self-service', { name, protocolVersion });
        if (response.code !== 200) {
          throw new Error(response.message || '申请客户端失败');
        }
        return response.data;
      } catch (error: any) {
        throw new Error(error.message || '申请客户端失败');
      }
    },

    // 更新客户端回调地址（仅限本人拥有的）
    async updateClientRedirectUri(id: number, redirectUri: string) {
      try {
        const response = await request.put<any, ApiResponse<any>>(`/user/oauth2/clients/${id}/redirect-uri`, {
          redirectUri
        });
        if (response.code !== 200) {
          throw new Error(response.message || '更新回调地址失败');
        }
        return response.data;
      } catch (error: any) {
        throw new Error(error.message || '更新回调地址失败');
      }
    },
    async sendOAuthClientSecretCode(id: number) {
      const response = await request.post<void, ApiResponse<any>>(`/user/oauth2/clients/${id}/secret/email-code`)
      if (response.code !== 200) throw new Error(response.message || '验证码发送失败')
      return response.data
    },
    async revealOAuthClientSecret(id: number, method: 'password' | 'email', credential: string) {
      const response = await request.post<any, ApiResponse<any>>(`/user/oauth2/clients/${id}/secret/reveal`, {
        method,
        credential
      })
      if (response.code !== 200) throw new Error(response.message || '身份验证失败')
      return response.data
    },
    async login(data: LoginData) {
      try {
        const response = await request.post<LoginData, ApiResponse<LoginResponse>>('/auth/login', data);
        
        if (response.code !== 200 || !response.data) {
          throw new Error(response.message || '登录失败');
        }
        
        this.token = response.data.token;
        this.userInfo = response.data.userInfo;
        localStorage.setItem('token', response.data.token);
        
        // 新增：通知其他标签页登录状态变化
        this.notifyLoginStatusChange();
        
        return response.data;
      } catch (error: any) {
        throw new Error(error.message || '登录失败');
      }
    },

    async loginWithEmail(data: EmailLoginData) {
      try {
        const response = await request.post<EmailLoginData, ApiResponse<LoginResponse>>('/auth/login/email', data);
        
        if (response.code !== 200 || !response.data) {
          throw new Error(response.message || '邮箱登录失败');
        }
        
        this.token = response.data.token;
        this.userInfo = response.data.userInfo;
        localStorage.setItem('token', response.data.token);
        
        // 新增：通知其他标签页登录状态变化
        this.notifyLoginStatusChange();
        
        return response.data;
      } catch (error: any) {
        throw new Error(error.message || '邮箱登录失败');
      }
    },

    async getUserInfo() {
      try {
        const response = await request.get('/user/info')
        if (response.data) {
          this.userInfo = response.data
          return response.data
        }
        throw new Error('获取用户信息失败')
      } catch (error) {
        console.error('获取用户信息错误:', error)
        throw error
      }
    },

    async getUserProfile() {
      try {
        const response = await request.get('/user/profile')
        if (response.data) {
          // 更新用户基本信息
          this.userInfo = response.data.userInfo
          return response.data
        }
        throw new Error('获取用户档案失败')
      } catch (error) {
        console.error('获取用户档案错误:', error)
        throw error
      }
    },

    async updateUserInfo(userData: Partial<UserInfo>) {
      const response = await request.put<Partial<UserInfo>, ApiResponse<UserInfo>>('/user/info', userData)
      if (response.data) {
        this.userInfo = { ...this.userInfo, ...response.data }
        return response.data
      }
      throw new Error('更新用户信息失败')
    },

    async changePassword(data: {
      oldPassword: string;
      newPassword: string;
    }) {
      try {
        const response = await request.post<typeof data, ApiResponse<void>>('/auth/password/update', data);
        if (response.code !== 200) {
          throw new Error(response.message || '修改密码失败');
        }
        return true;
      } catch (error: any) {
        throw new Error(error.message || '修改密码失败');
      }
    },

    async resetPasswordByEmail(data: {
      email: string;
      code: string;
      newPassword: string;
    }) {
      try {
        const response = await request.post<typeof data, ApiResponse<void>>('/auth/password/reset', data);
        if (response.code !== 200) {
          throw new Error(response.message || '重置密码失败');
        }
        return true;
      } catch (error: any) {
        throw new Error(error.message || '重置密码失败');
      }
    },

    logout() {
      this.token = null;
      this.userInfo = null;
      localStorage.removeItem('token');
      
      // 新增：通知其他标签页登录状态变化
      this.notifyLoginStatusChange();
    },

    async checkAuth() {
      if (!this.token) return;
      try {
        const result = await request.get<void, UserInfo>('/verify-token');
        this.userInfo = result;
      } catch (error) {
        this.logout();
        throw error;
      }
    },

    async unbindQQ() {
      try {
        const response = await request.post<void, ApiResponse<void>>('/auth/qq/unbind');
        if (response.code !== 200) {
          throw new Error(response.message || '解绑QQ失败');
        }
        await this.getUserInfo(); // 刷新用户信息
        return true;
      } catch (error: any) {
        throw new Error(error.message || '解绑QQ失败');
      }
    },

    async registerWithEmail(data: { email: string, code: string, lotNumber: string, captchaOutput: string, passToken: string, genTime: string }) {
      try {
        const response = await request.post<RegisterRequest, ApiResponse<LoginResponse>>('/auth/register', {
          email: data.email,
          emailCode: data.code,
          lotNumber: data.lotNumber,
          captchaOutput: data.captchaOutput,
          passToken: data.passToken,
          genTime: data.genTime
        });
        
        if (response.code !== 200 || !response.data) {
          throw new Error(response.message || '注册失败');
        }
        
        this.token = response.data.token;
        this.userInfo = response.data.userInfo;
        localStorage.setItem('token', response.data.token);
        
        // 新增：通知其他标签页登录状态变化
        this.notifyLoginStatusChange();
        
        return response.data;
      } catch (error: any) {
        console.error('注册失败：', error);
        throw new Error(error.message || '注册失败');
      }
    },

    async getCaptcha() {
      try {
        const response = await request.get<void, ApiResponse<CaptchaResponse>>('/auth/captcha');
        if (response.code === 200 && response.data) {
          this.captchaId = response.data.id;
          return response.data;
        }
        throw new Error('获取验证码失败');
      } catch (error: any) {
        throw new Error(error.message || '获取验证码失败');
      }
    },

    async sendEmailCode(data: SendEmailCodeRequest) {
      try {
        const response = await request.post<SendEmailCodeRequest, ApiResponse<void>>('/auth/email/code', data);
        if (response.code !== 200) {
          throw new Error(response.message || '发送验证码失败');
        }
      } catch (error: any) {
        throw new Error(error.message || '发送验证码失败');
      }
    },

    async sendEmailCodeWithGeetest(data: SendEmailCodeWithGeetestRequest) {
      try {
        const response = await request.post<SendEmailCodeWithGeetestRequest, ApiResponse<void>>('/auth/email/code/geetest', data);
        if (response.code !== 200) {
          throw new Error(response.message || '发送验证码失败');
        }
      } catch (error: any) {
        throw new Error(error.message || '发送验证码失败');
      }
    },

    async updateEmail(data: UpdateEmailRequest) {
      try {
        const response = await request.post<UpdateEmailRequest, ApiResponse<void>>('/auth/email/update', {
          newEmail: data.newEmail,
          code: data.emailCode
        });
        
        if (response.code !== 200) {
          throw new Error(response.message || '修改邮箱失败');
        }
        await this.getUserInfo();
      } catch (error: any) {
        throw new Error(error.message || '修改邮箱失败');
      }
    },

    async loginWithQQ(data: QQLoginRequest) {
      try {
        const response = await request.post<QQLoginRequest, ApiResponse<LoginResponse>>('/auth/login/qq', data);
        
        if (response.code !== 200 || !response.data) {
          throw new Error(response.message || 'QQ登录失败');
        }
        
        this.token = response.data.token;
        this.userInfo = response.data.userInfo;
        localStorage.setItem('token', response.data.token);
        
        // 新增：通知其他标签页登录状态变化
        this.notifyLoginStatusChange();
        
        return response.data;
      } catch (error: any) {
        throw new Error(error.message || 'QQ登录失败');
      }
    },

    async bindQQ(data: { state: string; accessToken: string }) {
      try {
        const response = await request.post<QQBindRequest, ApiResponse<void>>('/auth/qq/bind', data);
        if (response.code !== 200) {
          throw new Error(response.message || '绑定QQ失败');
        }
        await this.getUserInfo(); // 刷新用户信息
        return true;
      } catch (error: any) {
        throw new Error(error.message || '绑定QQ失败');
      }
    },

    async getQQBinding() {
      try {
        const response = await request.get<void, ApiResponse<QQBindingResponse>>('/auth/qq/binding');
        if (response.code !== 200 || !response.data) {
          throw new Error(response.message || '获取QQ绑定信息失败');
        }
        return response.data;
      } catch (error: any) {
        throw new Error(error.message || '获取QQ绑定信息失败');
      }
    },

    async loginWithGoogle(data: GoogleLoginRequest) {
      try {
        const response = await request.post<GoogleLoginRequest, ApiResponse<LoginResponse>>('/auth/login/google', data);
        if (response.code !== 200 || !response.data) throw new Error(response.message || 'Google 登录失败');
        this.token = response.data.token;
        this.userInfo = response.data.userInfo;
        localStorage.setItem('token', response.data.token);
        this.notifyLoginStatusChange();
        return response.data;
      } catch (e: any) { throw new Error(e.message || 'Google 登录失败'); }
    },

    async loginWithGithub(data: GithubLoginRequest) {
      try {
        const response = await request.post<GithubLoginRequest, ApiResponse<LoginResponse>>('/auth/login/github', data);
        if (response.code !== 200 || !response.data) throw new Error(response.message || 'GitHub 登录失败');
        this.token = response.data.token;
        this.userInfo = response.data.userInfo;
        localStorage.setItem('token', response.data.token);
        this.notifyLoginStatusChange();
        return response.data;
      } catch (e: any) { throw new Error(e.message || 'GitHub 登录失败'); }
    },

    async loginWithBohrium(data: { code: string }) {
      try {
        const response = await request.post<typeof data, ApiResponse<LoginResponse>>('/auth/bohrium/login', data);
        if (response.code !== 200 || !response.data) throw new Error(response.message || 'Bohrium 登录失败');
        this.token = response.data.token;
        this.userInfo = response.data.userInfo;
        localStorage.setItem('token', response.data.token);
        this.notifyLoginStatusChange();
        return response.data;
      } catch (e: any) { throw new Error(e.message || 'Bohrium 登录失败'); }
    },

    async bindBohrium(data: { code: string }) {
      const response = await request.post<typeof data, ApiResponse<void>>('/auth/bohrium/bind', data);
      if (response.code !== 200) throw new Error(response.message || '绑定 Bohrium 失败');
      return true;
    },

    async unbindBohrium() {
      const response = await request.post<void, ApiResponse<void>>('/auth/bohrium/unbind');
      if (response.code !== 200) throw new Error(response.message || '解绑 Bohrium 失败');
    },

    async getBohriumBinding() {
      const response = await request.get<void, ApiResponse<BohriumBindingResponse>>('/auth/bohrium/binding');
      if (response.code !== 200 || !response.data) throw new Error(response.message || '获取 Bohrium 绑定信息失败');
      return response.data;
    },

    async verifyDeletionWithBohrium(data: { code: string }) {
      const response = await request.post<typeof data, ApiResponse<{ ticket: string; expiresAt: string }>>('/auth/bohrium/deletion/verify', data);
      if (response.code !== 200 || !response.data) throw new Error(response.message || 'Bohrium 身份验证失败');
      return response.data;
    },

    async getAccountDeletionMethods() {
      const response = await request.get<void, ApiResponse<any>>('/user/account/deletion/methods');
      if (response.code !== 200 || !response.data) throw new Error(response.message || '获取注销验证方式失败');
      return response.data;
    },

    async sendAccountDeletionEmailCode() {
      const response = await request.post<void, ApiResponse<void>>('/user/account/deletion/email-code');
      if (response.code !== 200) throw new Error(response.message || '发送验证码失败');
    },

    async verifyAccountDeletion(data: { method: string; credential: string }) {
      const response = await request.post<typeof data, ApiResponse<{ ticket: string; expiresAt: string }>>('/user/account/deletion/verify', data);
      if (response.code !== 200 || !response.data) throw new Error(response.message || '身份验证失败');
      return response.data;
    },

    async completeAccountDeletion(data: { ticket: string; confirmation: string }) {
      const response = await request.post<typeof data, ApiResponse<void>>('/user/account/deletion/complete', data);
      if (response.code !== 200) throw new Error(response.message || '账号注销失败');
    },

    async bindGoogle(data: { code: string }) {
      try {
        const response = await request.post<{ code: string }, ApiResponse<void>>('/auth/google/bind', data);
        if (response.code !== 200) throw new Error(response.message || '绑定 Google 失败');
        await this.getUserInfo();
        return true;
      } catch (e: any) { throw new Error(e.message || '绑定 Google 失败'); }
    },

    async bindGithub(data: { code: string }) {
      try {
        const response = await request.post<{ code: string }, ApiResponse<void>>('/auth/github/bind', data);
        if (response.code !== 200) throw new Error(response.message || '绑定 GitHub 失败');
        await this.getUserInfo();
        return true;
      } catch (e: any) { throw new Error(e.message || '绑定 GitHub 失败'); }
    },

    async unbindGoogle() {
      const response = await request.post<void, ApiResponse<void>>('/auth/google/unbind');
      if (response.code !== 200) throw new Error(response.message || '解绑 Google 失败');
      await this.getUserInfo();
    },

    async unbindGithub() {
      const response = await request.post<void, ApiResponse<void>>('/auth/github/unbind');
      if (response.code !== 200) throw new Error(response.message || '解绑 GitHub 失败');
      await this.getUserInfo();
    },

    async getGoogleBinding() {
      const response = await request.get<void, ApiResponse<GoogleBindingResponse>>('/auth/google/binding');
      if (response.code !== 200 || !response.data) throw new Error(response.message || '获取 Google 绑定信息失败');
      return response.data;
    },

    async getGithubBinding() {
      const response = await request.get<void, ApiResponse<GithubBindingResponse>>('/auth/github/binding');
      if (response.code !== 200 || !response.data) throw new Error(response.message || '获取 GitHub 绑定信息失败');
      return response.data;
    },

    async getDeviceSessions() {
      try {
        // 从JWT中提取当前会话ID
        const token = this.token;
        let currentSessionId = null;
        
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            currentSessionId = payload.sessionId;
          } catch (error) {
            console.warn('无法解析JWT中的sessionId');
          }
        }
        
        const params = currentSessionId ? { currentSessionId } : {};
        const response = await request.get<void, ApiResponse<UserSessionResponse[]>>('/user/sessions', { params });
        if (response.code !== 200) {
          throw new Error(response.message || '获取设备列表失败');
        }
        return response.data;
      } catch (error: any) {
        console.error('获取设备会话失败:', error)
        throw new Error(error.message || '获取设备列表失败');
      }
    },

    async logoutDevice(sessionId: string) {
      try {
        const response = await request.delete<void, ApiResponse<void>>(`/user/sessions/${sessionId}`);
        if (response.code !== 200) {
          throw new Error(response.message || '下线设备失败');
        }
      } catch (error: any) {
        throw new Error(error.message || '下线设备失败');
      }
    },

    async logoutOtherDevices() {
      try {
        const response = await request.delete<void, ApiResponse<void>>('/user/sessions/all');
        if (response.code !== 200) {
          throw new Error(response.message || '下线所有设备失败');
        }
      } catch (error: any) {
        console.error('下线所有设备失败:', error)
        throw new Error(error.message || '下线所有设备失败');
      }
    },

    // 获取OAuth2授权记录
    async getOAuth2Authorizations(page = 1, size = 10) {
      try {
        // 确保参数类型正确
        const pageNumber = typeof page === 'number' ? page : parseInt(String(page)) || 1
        const sizeNumber = typeof size === 'number' ? size : parseInt(String(size)) || 10
        
        const response = await request.get('/user/oauth2/authorizations', {
          params: { page: pageNumber, size: sizeNumber }
        })
        return response.data
      } catch (error: any) {
        throw new Error(error.response?.data?.message || '获取授权记录失败')
      }
    },

    // 获取单个应用的OAuth2授权详情（分页）
    async getOAuth2AuthorizationDetails(clientId: string, page = 1, size = 10) {
      try {
        const response = await request.get(`/user/oauth2/authorizations/${clientId}/details`, {
          params: { page, size }
        })
        return response.data
      } catch (error: any) {
        throw new Error(error.response?.data?.message || '获取授权详情失败')
      }
    },

    // 取消OAuth2授权（按clientId软删除）
    async revokeOAuth2Authorization(clientId: string) {
      try {
        await request.put(`/user/oauth2/authorizations/${clientId}/revoke`)
      } catch (error: any) {
        throw new Error(error.response?.data?.message || '取消授权失败')
      }
    },

    // 检查用户对某客户端是否有有效授权
    async checkOAuth2Consent(clientId: string): Promise<boolean> {
      try {
        const response = await request.get('/oauth2/check-consent', {
          params: { client_id: clientId }
        })
        return response.data?.hasValidAuthorization === true
      } catch (error: any) {
        return false
      }
    },

    // 新增：通知其他标签页登录状态变化的方法
    notifyLoginStatusChange() {
      // 使用自定义事件来通知其他标签页
      window.dispatchEvent(new CustomEvent('loginStatusChanged', {
        detail: { token: this.token }
      }));
      
      // 同时更新sessionStorage中的最后已知token
      if (this.token) {
        sessionStorage.setItem('last_known_token', this.token);
      } else {
        sessionStorage.removeItem('last_known_token');
      }
    },

    // 通行密钥相关方法
    // 开始注册通行密钥
    async startPasskeyRegistration() {
      try {
        const response = await request.post('/user/passkey/register/start')
        return response.data
      } catch (error: any) {
        throw new Error(error.response?.data?.message || '开始注册通行密钥失败')
      }
    },

    // 完成注册通行密钥
    async finishPasskeyRegistration(challenge: string, credential: string, name?: string) {
      try {
        await request.post('/user/passkey/register/finish', {
          challenge,
          credential,
          name
        })
      } catch (error: any) {
        throw new Error(error.response?.data?.message || '完成注册通行密钥失败')
      }
    },

    // 获取用户的通行密钥列表
    async getPasskeys() {
      try {
        const response = await request.get('/user/passkeys')
        return response.data
      } catch (error: any) {
        throw new Error(error.response?.data?.message || '获取通行密钥列表失败')
      }
    },

    // 删除通行密钥
    async deletePasskey(passkeyId: number) {
      try {
        await request.delete(`/user/passkeys/${passkeyId}`)
      } catch (error: any) {
        throw new Error(error.response?.data?.message || '删除通行密钥失败')
      }
    },

    // 开始通行密钥登录（发现模式，不需要用户名）
    async startPasskeyLogin() {
      try {
        const response = await request.post('/auth/login/passkey/start')
        return response.data
      } catch (error: any) {
        throw new Error(error.response?.data?.message || '开始通行密钥登录失败')
      }
    },

    // 完成通行密钥登录
    async finishPasskeyLogin(challenge: string, assertion: string) {
      try {
        const response = await request.post<{ challenge: string; assertion: string }, ApiResponse<LoginResponse>>('/auth/login/passkey/finish', {
          challenge,
          assertion
        })
        
        if (response.code !== 200 || !response.data) {
          throw new Error(response.message || '登录失败')
        }
        
        this.token = response.data.token
        this.userInfo = response.data.userInfo
        localStorage.setItem('token', response.data.token)
        
        // 通知其他标签页登录状态变化
        this.notifyLoginStatusChange()
        
        return response.data
      } catch (error: any) {
        throw new Error(error.message || '通行密钥登录失败')
      }
    }
  },
});
